import type { PrintDocument } from '@aeris/printing'

import type { DeliveryRouteView } from './api.ts'
import { clockAfter, durationText, kmText, moneyText } from './route-format.ts'

/**
 * A folha da rota (F267): o que o entregador leva no bolso — a ordem, o
 * cliente, o endereço, o que cobrar e o horário previsto — e a assinatura na
 * volta. A4 ou bobina, pela impressão de sempre.
 *
 * O valor a cobrar é o título "na entrega" aberto quando a rota já saiu (o
 * total menos o sinal); antes de sair, o total do pedido.
 */
export const routePrintFor = (
  route: DeliveryRouteView,
  company: { readonly name: string },
  now: Date
): PrintDocument => {
  const departure =
    route.dispatchedAt === null ? now : new Date(route.dispatchedAt)
  const toCollect = (stop: DeliveryRouteView['stops'][number]): string =>
    route.status === 'planned'
      ? moneyText(stop.totalAmount)
      : stop.dueAmount === null
        ? 'já pago'
        : moneyText(stop.dueAmount)
  return {
    kind: 'report',
    title: `Rota de ${route.courierName}`,
    blocks: [
      { kind: 'text', text: company.name, align: 'center', bold: true },
      {
        kind: 'text',
        text: `Rota de ${route.courierName} (${route.mode === 'foot' ? 'a pé' : 'moto'})`,
        align: 'center',
        bold: true,
      },
      { kind: 'rule' },
      { kind: 'pair', label: 'Saída', value: clockAfter(departure, 0) },
      {
        kind: 'pair',
        label: 'Volta prevista',
        value:
          route.returnSeconds === null
            ? '—'
            : `~${clockAfter(departure, route.returnSeconds)}`,
      },
      {
        kind: 'pair',
        label: 'Entregas',
        value: `${route.stops.length} · ${durationText(route.durationSeconds)} · ${kmText(route.distanceMeters)}`,
      },
      { kind: 'rule' },
      {
        kind: 'table',
        columns: [
          { label: '#', align: 'right' },
          { label: 'Cliente e endereço' },
          { label: 'Pedido' },
          { label: 'Cobrar', align: 'right' },
          { label: 'Hora', align: 'right' },
        ],
        rows: route.stops.map((stop) => [
          String(stop.position),
          `${stop.customerName} — ${stop.addressLabel}`,
          `nº ${stop.number ?? '—'}`,
          toCollect(stop),
          `~${clockAfter(departure, stop.arrivalSeconds)}`,
        ]),
      },
      { kind: 'feed', lines: 2 },
      { kind: 'text', text: '_______________________________' },
      { kind: 'text', text: `Assinatura de ${route.courierName}` },
      { kind: 'cut' },
    ],
  }
}
